import { type ClientProfile, kustomerProfileOptions, type Task } from '@cartwheel/api/v1'
import { formatPhoneNumber } from '@cartwheel/string-utils'
import { useQuery } from '@tanstack/react-query'
import kustomerIconSrc from '../assets/kustomer.svg'
import { trackTaskResourceLink } from './task-analytics'

const SUPPORTED_TASK_TYPES = new Set([
  'schedule_intake',
  'schedule_psych_intake',
  'verify_insurance_and_send_cost_estimate',
])

export function TaskGuardianCard({ task, client }: { task: Task; client?: ClientProfile }) {
  if (!SUPPORTED_TASK_TYPES.has(task.type)) return null
  if (!client) return null

  const guardian = client.primary_guardian
  if (guardian) {
    return (
      <ContactCard
        name={guardian.name ?? 'Primary guardian'}
        phoneNumber={guardian.phone_number}
        roleLabel="Guardian 1"
        contactId={guardian.id}
        task={task}
      />
    )
  }

  // Adult-client fallback. ClientProfile currently exposes no phone_number or
  // contact_id, so the card renders name + role only until a follow-up ticket
  // enriches the serializer (see CCA-1708 open question).
  return <ContactCard name={client.name} roleLabel="Client" />
}

function ContactCard({
  name,
  phoneNumber,
  roleLabel,
  contactId,
  task,
}: {
  name: string
  phoneNumber?: string | null
  roleLabel: string
  contactId?: string
  task?: Task
}) {
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-sand bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4">
        <div>
          <div className="flex h-6 w-full items-center justify-between gap-2">
            <h3 className="min-w-0 truncate text-base font-semibold leading-[1.5] text-charcoal">
              {name}
            </h3>
            <span className="shrink-0 rounded-lg bg-lavender-25 px-2 py-0.5 text-xs font-semibold leading-[1.5] text-indigo">
              {roleLabel}
            </span>
          </div>
          {phoneNumber && (
            <p className="text-sm leading-[1.5] text-charcoal-50">
              {formatPhoneNumber(phoneNumber)}
            </p>
          )}
        </div>
        {contactId && task && <KustomerLink contactId={contactId} task={task} />}
      </div>
    </div>
  )
}

function KustomerLink({ contactId, task }: { contactId: string; task: Task }) {
  const { data } = useQuery({
    ...kustomerProfileOptions({ path: { external_id: contactId } }),
    retry: false,
  })

  if (!data?.url) return null

  return (
    <a
      href={data.url}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackTaskResourceLink(task, 'kustomer')}
      className="flex items-center gap-1 text-sm font-semibold leading-[1.5] text-forest hover:underline"
    >
      <img alt="" className="size-4 shrink-0" src={kustomerIconSrc} aria-hidden="true" />
      Kustomer
    </a>
  )
}
